import React from 'react'
import { RenderDialogProps } from '../../../legacy-design-system/action/confirmation-action'
import { ConfirmationDialog } from '../../../legacy-design-system/modal'
import { FormattedMessage, useIntl } from 'react-intl'
import { Dropdown } from '../../../legacy-design-system/dropdown'
import { css } from '@emotion/css'
import gql from 'graphql-tag'
import { Mutation } from 'react-apollo'
import { MutationProps } from './merge-action'
import { Role } from '@contrast-security-inc/base-types'
import { logError, logSuccess } from '../../toaster'
import { DateTime } from '../shared'

export interface Vulnerability {
  readonly id: string
  readonly title: string
  readonly lastDetected: DateTime
  readonly application: {
    readonly id: string
    readonly role: Role
  }
}

interface MergeDialogProps extends RenderDialogProps {
  readonly organizationID: string
  readonly vulnerabilities: Vulnerability[]
}

interface State {
  readonly selectedVulnerability: Vulnerability
}

export class ConfirmMergeVulnerabilitiesDialog extends React.Component<
  MergeDialogProps & {
    readonly merge: (primaryVulnerabilityID: string) => Promise<any>
  },
  State
> {
  constructor(props) {
    super(props)

    if (props.vulnerabilities.length <= 1) {
      throw new Error('There must be at least two vulnerabilities for merging.')
    }

    this.state = {
      selectedVulnerability: props.vulnerabilities[0]
    }
  }

  public render() {
    const { vulnerabilities, merge, isOpen, closeDialog } = this.props
    const { selectedVulnerability } = this.state

    return (
      <ConfirmationDialog
        size="large"
        isOpen={isOpen}
        onCancel={closeDialog}
        onConfirm={async () => {
          closeDialog()
          await merge(selectedVulnerability.id)
        }}
        renderConfirmMessage={() => <FormattedMessage id="action_merge" />}
        renderCancelMessage={() => <FormattedMessage id="action_cancel" />}
        renderTitle={() => (
          <FormattedMessage id="title_vulnerabilities_mergeVulnerabilities" />
        )}
        renderBody={() => {
          return (
            <div>
              <FormattedMessage id="detail_mergeVulnerabilities_mergeCanNotBeUndone">
                {(message) => <p>{message}</p>}
              </FormattedMessage>
              <FormattedMessage id="detail_mergeVulnerabilities_selectRepresentativeVuln">
                {(message) => <p>{message}</p>}
              </FormattedMessage>
              <Dropdown<Vulnerability>
                items={vulnerabilities}
                selectedItem={selectedVulnerability}
                itemToString={({ title, lastDetected }) =>
                  `${title} - ${lastDetected}`
                }
                itemToKey={({ id }) => id}
                onChange={this.onSelectedVulnerabilityChange}
                styleOverrides={{
                  outerContainerCss: css`
                    width: 100%;
                  `,
                  selectionTextCss: css`
                    width: 98%;
                  `
                }}
              />
            </div>
          )
        }}
      />
    )
  }

  private onSelectedVulnerabilityChange = (vulnerability: Vulnerability) => {
    this.setState({ selectedVulnerability: vulnerability })
  }
}

interface MutationResponse {
  readonly mergeVulnerabilities: {
    readonly _placeholder: boolean
    readonly __typename: string
  }
  readonly __typename: string
}

interface MutationVariables {
  readonly organizationID: string
  readonly ids: string[]
  readonly applicationID: string
  readonly primaryVulnerabilityID: string
}

export function MergeVulnerabilitiesDialog({
  organizationID,
  refresh,
  refetchQueries,
  vulnerabilities,
  ...props
}: MergeDialogProps & MutationProps) {
  const { formatMessage } = useIntl()

  return (
    <Mutation<MutationResponse, MutationVariables>
      mutation={gql`
        mutation MergeVulnerabilities(
          $organizationID: ID!
          $ids: [ID!]!
          $applicationID: ID!
          $primaryVulnerabilityID: ID!
        ) {
          mergeVulnerabilities(
            input: {
              organizationID: $organizationID
              ids: $ids
              applicationID: $applicationID
              primaryVulnerabilityID: $primaryVulnerabilityID
            }
          ) {
            _placeholder
          }
        }
      `}
    >
      {(mergeVulnerabilities) => {
        return (
          <ConfirmMergeVulnerabilitiesDialog
            {...props}
            vulnerabilities={vulnerabilities}
            organizationID={organizationID}
            merge={async (primaryVulnerabilityID) => {
              try {
                await mergeVulnerabilities({
                  refetchQueries,
                  variables: {
                    organizationID,
                    applicationID: vulnerabilities[0].application.id,
                    primaryVulnerabilityID,
                    ids: vulnerabilities
                      .map((vulnerability) => vulnerability.id)
                      .filter((id) => id !== primaryVulnerabilityID)
                  },
                  optimisticResponse: {
                    mergeVulnerabilities: {
                      _placeholder: true,
                      __typename: 'VulnerabilityBulkResponse'
                    },
                    __typename: 'Mutation'
                  }
                })
                await refresh()
                logSuccess(
                  formatMessage({
                    id: 'detail_vulnerabilities_successfullyMergedVulnerabilities'
                  })
                )
              } catch (error) {
                logError(
                  formatMessage({
                    id: 'detail_vulnerabilities_errorMergingVulnerabilities'
                  })
                )
                return error
              }
            }}
          />
        )
      }}
    </Mutation>
  )
}
